import { DispatchWithoutAction, useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { SingleOptTimeRangePicker } from "components/date/SingleOptTimePeriodPicker"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { ModalFreeSpinGame } from "components/modals/ModalFreeSpinGame"
import { Table } from "components/tables/Table"

import { PlayerBonusManager } from "features/bonus2/components/player-bonuses/PlayerBonusManager"
import { UserAccountActivityContent, UserBonusContent } from "features/user/tab-contents"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import {
  getUserBetsColumns,
  getUserTransactionsColumns,
  tableUserBetbyBets,
  tableUserReferrals,
  tableUserRewardGames,
  transactionStatuses,
  transactionTypes
} from "constants/data"
import { GameStatusType, SortOrderBy, SortOrderType } from "constants/eums"
import { PERMISSIONS } from "constants/permissions"

import { transactionService } from "services/transaction/transaction.service"
import { GameSessions, GetSingleUser } from "services/user/type"
import { userService } from "services/user/user.service"

import { Maybe } from "types/common"

import { formatFiatCurrency } from "util/formatter"

import { _currentTenant, isBonusV2Tenant, isMicroserviceTenant } from "config/featureFlags"

enum EnumTab {
  Bets = "Bets",
  Sports = "Sports",
  Transactions = "Transactions",
  Referrals = "Referrals",
  "Reward Games" = "Reward Games",
  Bonuses = "Bonuses",
  "Activity Logs" = "Activity Logs"
}

interface UserDetailTabsProps {
  forceUpdate: DispatchWithoutAction
  updateKey: any
  userResult: Maybe<GetSingleUser>
  onLoad: () => void
  setUserResult: (v: any) => void
  gameSessions: GameSessions[] | undefined
}

export const UserTabContents = ({ forceUpdate, updateKey, userResult, onLoad, setUserResult, gameSessions }: UserDetailTabsProps) => {
  const { t } = useTranslation(["translation"], { useSuspense: false })
  const { user } = useProps()
  const { has } = usePermissions()
  const [type, setType] = useState<string>("")
  const [status, setStatus] = useState<string>("")
  const [currentTab, setCurrentTab] = useState<EnumTab>(EnumTab.Bets)
  const [timeframe, setTimeframe] = useState<(Date | null)[]>([])
  const [confirmRow, setConfirmRow] = useState<any>(null)
  const [declineRow, setDeclineRow] = useState<any>(null)
  const [editRow, setEditRow] = useState<any>(null)
  const [openCreateModal, setOpenCreateModal] = useState(false)
  const [tableUpdatedAt, setTableUpdatedAt] = useState<number>(Date.now())
  // Memoized on the tenant mode, not computed inline: Table resets the operator's column
  // selection whenever the `columns` prop changes identity, which an inline getter does on every
  // parent render (a filter or tab change).
  const isMsTenant = isMicroserviceTenant()
  const userTransactionColumns = useMemo(() => getUserTransactionsColumns(), [isMsTenant])
  const canExport = !!user && has(PERMISSIONS.PAGE_ADMINS)

  const handleChangeTimeFrame = (dates: (Date | null)[]) => setTimeframe(dates)

  const onEditRow = (row: any): void => {
    setEditRow(row)
  }

  const actions = [
    {
      name: t("common.delete"),
      condition: (row: any) => row?.status === GameStatusType.Claim,
      handler: onEditRow
    }
  ]

  const confirmRowHandler = (): void => {
    setConfirmRow(null)
    setTableUpdatedAt(Date.now())
  }

  const onConfirmTransaction = async (): Promise<void> => {
    const response = await transactionService.confirmTransaction(confirmRow?.id)
    if (response) {
      toast.success(
        t("transactions.successAction", {
          actionText: t("common.confirmed")
        }) as string
      )
    }
  }

  const onOpenConfirmRow = (row: any): void => {
    setConfirmRow(row)
  }

  const onOpenDeclineRow = (row: any): void => {
    setDeclineRow(row)
  }

  const declineRowHandler = (): void => {
    setDeclineRow(null)
    setTableUpdatedAt(Date.now())
  }

  const onDeclineTransaction = async (): Promise<void> => {
    const response = await transactionService.declineTransaction(declineRow?.id)
    if (response) {
      toast.success(t("transactions.successAction", { actionText: t("common.declined") }) as string)
    }
  }

  const transactionActions = [
    {
      name: t("common.decline"),
      condition: (row: any) => row?.status === "UNCONFIRMED" && row?.type === "WITHDRAWAL",
      handler: onOpenDeclineRow,
      buttonColor: "btn-secondary"
    },
    {
      name: t("common.confirm"),
      condition: (row: any) => row?.status === "UNCONFIRMED" && row?.type === "WITHDRAWAL",
      handler: onOpenConfirmRow
    }
  ]

  const handleCancelBonusSuccess = () => {
    setUserResult((prev: any) => (prev ? { ...prev, busd_balance: "", coins: { ...prev.coins, B_USD: 0 } } : prev))
    onLoad()
    forceUpdate()
  }

  const onRewardGameDelete = useCallback(
    async (rewardGame: any): Promise<void> => {
      if (!userResult) return
      const response = await userService.deleteUserRewardGames(userResult.id, rewardGame.id)
      if (response) {
        // @ts-ignore
        toast.success(t("games.successDeleteRewardGame"))
        setTableUpdatedAt(Date.now())
      }
    },
    [userResult]
  )

  const onAddRewardGames = useCallback(
    async ({ game }: any): Promise<void> => {
      if (!userResult) return
      const response = await userService.addUserRewardGames(userResult.id, game)
      if (response) {
        // @ts-ignore
        toast.success(t("games.successAddedRewardGame"))
      }
    },
    [userResult]
  )

  const createFreeSpinGameHandler = useCallback((): void => {
    setOpenCreateModal(false)
    setTableUpdatedAt(Date.now())
  }, [])

  return userResult ? (
    <>
      <div className="flex items-center justify-center pt-6 border-t border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)]">
        <div className="tabs gap-2">
          {Object.keys(EnumTab).map((tab) => (
            <a
              key={`user-tab-${tab}`}
              onClick={() => setCurrentTab(tab as EnumTab)}
              className={clsx(
                "flex cursor-pointer items-center text-left py-3 rounded-lg px-4 text-base border text-[rgba(6,7,21,1)] dark:text-white border-[rgba(132,_0,_168,_1)] dark:border-transparent transition-all dark:bg-[#0C0C1A] hover:border-[rgba(255,255,255,0.1)]",
                {
                  "transition-all text-white bg-[rgba(132,_0,_168,_1)] dark:bg-[rgba(132,_0,_168,_1)]": currentTab === tab,
                  "hover:border-[rgba(132,_0,_168,_0.8)] hover:dark:border-[rgba(132,_0,_168,_0.8)]  hover:bg-[rgba(169,78,206,0.2)] dark:hover:bg-[rgba(169,78,206,0.15)]":
                    currentTab !== tab
                }
              )}
            >
              {tab}
            </a>
          ))}
        </div>
      </div>

      {currentTab !== EnumTab["Reward Games"] ? (
        <div className="max-w-md z-20">
          <SingleOptTimeRangePicker onTimeframeChange={handleChangeTimeFrame} />
        </div>
      ) : null}

      {currentTab === EnumTab.Bets && (
        <Table
          title={t("stats.bets")}
          handler={userService.getUserBets}
          columns={getUserBetsColumns()}
          id={userResult.id}
          filters={{ createdAt: timeframe }}
          gameSessions={gameSessions}
          isExport={canExport}
          exportFileName="Bet_History"
        />
      )}

      {currentTab === EnumTab.Sports && (
        <Table
          title="Sports Bets"
          handler={userService.getUserBetbyBets}
          columns={tableUserBetbyBets}
          id={userResult.id}
          filters={{ createdAt: timeframe }}
          gameSessions={gameSessions}
          isExport={canExport}
          exportFileName="Sports_Bet_History"
        />
      )}

      {currentTab === EnumTab.Transactions && (
        <>
          <div className="flex flex-row flex-1 max-w-md space-x-4">
            <DropdownMenu
              title="Transaction Type"
              options={transactionTypes}
              value={type}
              onChange={(e: any) => setType(e.target.value)}
              rounded
            />
            <DropdownMenu
              title="Transaction Status"
              options={transactionStatuses}
              value={status}
              onChange={(e: any) => setStatus(e.target.value)}
              rounded
            />
          </div>
          <Table
            title={t("transactions.title")}
            handler={userService.getUserTransactions}
            columns={userTransactionColumns}
            id={userResult.id}
            filters={{ type, status, createdAt: timeframe }}
            actions={transactionActions}
            isExport={canExport}
            exportFileName="Transactions"
          />
          <ModalConfirmAction
            data={confirmRow}
            handler={confirmRowHandler}
            text={t("modal.confirmTransaction", {
              actionText: t("common.confirm"),
              usd_amount: formatFiatCurrency(confirmRow?.usd_amount),
              user: confirmRow?.user
            })}
            actionText={t("common.confirm")}
            actionHandler={onConfirmTransaction}
          />
          <ModalConfirmAction
            data={declineRow}
            handler={declineRowHandler}
            text={t("modal.confirmTransaction", {
              actionText: t("common.decline"),
              usd_amount: formatFiatCurrency(declineRow?.usd_amount),
              user: declineRow?.user
            })}
            actionText={t("common.decline")}
            actionHandler={onDeclineTransaction}
          />
        </>
      )}

      {currentTab === EnumTab.Referrals && (
        <Table
          title={t("referrals.referrals")}
          handler={userService.getUserReferrals}
          columns={tableUserReferrals}
          filters={{ createdAt: timeframe }}
          id={userResult.id}
        />
      )}

      {currentTab === EnumTab["Reward Games"] && (
        <Table
          title={t("games.rewardGames")}
          handler={userService.getUserRewardGames}
          columns={tableUserRewardGames}
          id={userResult.id}
          actions={actions}
          updatedAt={tableUpdatedAt}
          buttonHander={() => setOpenCreateModal(true)}
          buttonText={t("common.create")}
          defaultSortOrder={SortOrderType.ASC}
          defaultSortOrderBy={SortOrderBy.ID}
        />
      )}

      {/* Fix 04b — bonus data source depends on tenant mode:
          - microservice tenant → bonus-service PlayerBonus (V2 PlayerBonusManager, scoped to
            this user), the authoritative source the player actually played with.
          - monolith tenant → legacy monolith UserBonus table (unchanged). */}
      {currentTab === EnumTab.Bonuses &&
        (isBonusV2Tenant() ? (
          <PlayerBonusManager
            key={`user-bonus2-${userResult.id}-${updateKey}`}
            tenant={_currentTenant ?? ""}
            fixedUserId={userResult.id}
            fixedUsername={userResult.username}
          />
        ) : (
          <UserBonusContent
            userId={userResult.id}
            onSuccessCancel={handleCancelBonusSuccess}
            key={`user-bonus-tale-wrapper-${updateKey}`}
            filters={{ createdAt: timeframe }}
          />
        ))}

      {currentTab === EnumTab["Activity Logs"] && <UserAccountActivityContent userId={userResult.id} filters={{ createdAt: timeframe }} />}

      <ModalFreeSpinGame open={openCreateModal} handler={createFreeSpinGameHandler} onSubmit={onAddRewardGames} />
      <ModalConfirmAction
        data={editRow}
        handler={() => {
          setEditRow(null)
          setTableUpdatedAt(Date.now())
        }}
        text={t("modal.confirmAction", {
          actionText: t("common.delete"),
          title: editRow?.title,
          id: editRow?.id
        })}
        actionText={t("common.delete")}
        actionHandler={onRewardGameDelete}
      />
    </>
  ) : null
}
